import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  Grid,
  Button,
  IconButton,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  Check,
  CreditCard,
  CalendarToday,
  Event,
  CheckCircle,
  Info,
  ErrorOutline,
  Refresh,
  ExpandMore,
  ExpandLess,
  TrendingUp,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { ActivePlan, fetchActivePlans, Plan, fetchPlans } from '../services/companyService';
import { STORAGE_KEYS, USER_ROLES } from '../helpers/constants';
import { getFetchErrorMessage } from '../helpers/errorUtils';
import { getZohoSubscriptionId } from '../helpers/storageUtils';
import { useAuth } from '../contexts/AuthContext';
import PlansModal from './PlansModal';
import { usePlanUpgrade } from '../hooks/usePlanUpgrade';

interface PlanDetailsProps {
  onPlanSelect?: (planId: string) => void;
  showDetailedInfo?: boolean;
  showCardBorder?: boolean; // Control whether to show card border (default: true for modal)
  showUpgradeButton?: boolean; // Control whether to show upgrade button (default: true, false when used in modal)
  onUpgrade?: () => void; // Callback for upgrade button click
}

const PlanDetails: React.FC<PlanDetailsProps> = ({
  onPlanSelect,
  showDetailedInfo = true,
  showCardBorder = true,
  showUpgradeButton = true,
  onUpgrade,
}) => {
  const { isDarkMode } = useTheme();
  const { user, markSessionExpired } = useAuth();
  const isUserRole = user?.role === USER_ROLES.USER;
  const [activePlan, setActivePlan] = useState<ActivePlan | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const [showErrorDetails, setShowErrorDetails] = useState<boolean>(false);
  const [plansModalOpen, setPlansModalOpen] = useState<boolean>(false);
  const [availablePlans, setAvailablePlans] = useState<Plan[]>([]);

  const { handlePlanSelect: handlePlanUpgrade } = usePlanUpgrade({
    currentPlanCode: activePlan?.plan_code,
    currentPlanName: activePlan?.plan_name,
    onError: (errorMsg) => {
      alert(`Failed to create payment page: ${errorMsg}. Please try again.`);
      setPlansModalOpen(true); // Reopen modal on error
    },
  });

  const loadActivePlan = async () => {
    try {
      setLoading(true);
      setError(null);
      setErrorDetails(null);
      setShowErrorDetails(false);

      // Get auth token from localStorage - use REFRESH_TOKEN first like other working services
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || 
                   localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) || 
                   undefined;

      const response = await fetchActivePlans(token);

      if (response.data) {
        setActivePlan(response.data);
      } else {
        const errorMsg = response.message || 'Failed to fetch active plan';
        setError(errorMsg);
        setErrorDetails('No plan data received from the API');
      }
    } catch (err) {
      console.error('Error fetching active plan:', err);
      
      let errorMessage = 'Failed to load plan details';
      let details = '';
      
      if (err instanceof Error) {
        // Use user-friendly message for network/fetch errors (e.g. connection refused)
        errorMessage = getFetchErrorMessage(err, 'Failed to load plan details');
        
        // Extract more details from error message
        if (err.message.includes('Unexpected response format')) {
          details = 'The API returned data in an unexpected format. Please check the API response structure.';
        } else if (err.message.includes('Failed to fetch') || err.message.toLowerCase().includes('typeerror')) {
          details = 'Network error occurred. Please check your internet connection and try again.';
        } else if (err.message.includes('401') || err.message.includes('Unauthorized')) {
          markSessionExpired();
          return;
        } else if (err.message.includes('403') || err.message.includes('Forbidden')) {
          details = 'You do not have permission to access this resource.';
        } else if (err.message.includes('404') || err.message.includes('Not Found')) {
          details = 'The requested resource was not found.';
        } else if (err.message.includes('500') || err.message.includes('Internal Server Error')) {
          details = 'Server error occurred. Please try again later or contact support.';
        }
      }
      
      setError(errorMessage);
      setErrorDetails(details || 'An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadActivePlan();
  }, []);

  // Handle upgrade button click
  const handleUpgradeClick = () => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      setPlansModalOpen(true);
    }
  };

  // Handle plan selection from PlansModal
  const handlePlanSelect = async (planId: string) => {
    try {
      await handlePlanUpgrade(planId, availablePlans, () => setPlansModalOpen(false));
    } catch (error) {
      // Error handling is done in usePlanUpgrade hook
    }
  };

  // Fetch plans when modal opens
  useEffect(() => {
    if (plansModalOpen && availablePlans.length === 0) {
      const loadPlans = async () => {
        try {
          const plansResponse = await fetchPlans();
          if (plansResponse.success) {
            setAvailablePlans(plansResponse.data);
          }
        } catch (err) {
          console.error('Error fetching plans:', err);
        }
      };
      loadPlans();
    }
  }, [plansModalOpen]);

  const formatPrice = (price: string, currencyCode: string = 'USD'): string => {
    const numPrice = Number(price);
    const formattedPrice = isNaN(numPrice) ? '0.00' : numPrice.toFixed(2);
    return `${currencyCode === 'USD' ? '$' : currencyCode} ${formattedPrice}`;
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const formatDateShort = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'info' => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'trial':
        return 'info';
      case 'expired':
      case 'cancelled':
        return 'error';
      default:
        return 'warning';
    }
  };

  if (loading) {
    return (
      <Box
        id="plan-details-loading"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress id="plan-details-loading-spinner" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box id="plan-details-error-container" sx={{ width: '100%' }}>
        <Alert 
          id="plan-details-error-alert"
          severity="error" 
          sx={{ 
            mb: 2,
            '& .MuiAlert-message': {
              width: '100%',
            }
          }}
          icon={<ErrorOutline />}
          action={
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {errorDetails && (
                <IconButton
                  id="plan-details-error-details-toggle"
                  size="small"
                  onClick={() => setShowErrorDetails(!showErrorDetails)}
                  sx={{ color: 'inherit' }}
                  aria-label="show error details"
                >
                  {showErrorDetails ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              )}
              <Button
                id="plan-details-error-retry-btn"
                color="inherit"
                size="small"
                onClick={loadActivePlan}
                startIcon={<Refresh />}
                sx={{ textTransform: 'none' }}
              >
                Retry
              </Button>
            </Box>
          }
        >
          <Typography id="plan-details-error-message" variant="body1" sx={{ fontWeight: 500, mb: errorDetails ? 0.5 : 0 }}>
            {error}
          </Typography>
          {errorDetails && (
            <Collapse id="plan-details-error-details" in={showErrorDetails}>
              <Typography id="plan-details-error-details-text" variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                {errorDetails}
              </Typography>
            </Collapse>
          )}
        </Alert>
        
        {/* Additional help text */}
        <Card 
          id="plan-details-error-help"
          sx={{ 
            p: 2, 
            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
            border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Info sx={{ fontSize: 20, color: 'text.secondary', mt: 0.25 }} />
            <Box>
              <Typography variant="body2" color="text.secondary">
                <strong>Need help?</strong> If this issue persists, please:
              </Typography>
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                <Typography component="li" variant="body2" color="text.secondary">
                  Check your internet connection
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  Verify you're logged in correctly
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  Contact support if the problem continues
                </Typography>
              </Box>
            </Box>
          </Box>
        </Card>
      </Box>
    );
  }

  if (!activePlan) {
    return (
      <Alert id="plan-details-no-plan" severity="info" sx={{ mb: 3 }}>
        No active plan found.
      </Alert>
    );
  }

  // Convert features object to array for display
  const featuresArray = activePlan.features 
    ? Object.entries(activePlan.features).map(([key, value]) => value)
    : [];

  const cardContent = (
    <CardContent id="plan-details-card-content" sx={{ p: 1.5, pb: 0.5 }}>
          {/* Header */}
          <Box id="plan-details-header" sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.75 }}>
              <Box sx={{ flex: 1 }}>
                <Typography id="plan-details-plan-name" variant="h5" component="h2" sx={{ fontWeight: 700, mb: 0.25, color: 'text.primary' }}>
                  {activePlan.plan_name}
                </Typography>
                <Typography id="plan-details-plan-code" variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  Plan Code: {activePlan.plan_code}
                </Typography>
              </Box>
              <Box id="plan-details-status-container" sx={{ display: 'flex', gap: 0.75, alignItems: 'center', ml: 2 }}>
                <Chip
                  id="plan-details-status-chip"
                  label={activePlan.status.charAt(0).toUpperCase() + activePlan.status.slice(1)}
                  size="small"
                  color={getStatusColor(activePlan.status)}
                  sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                />
                {activePlan.is_trial && (
                  <Chip
                    id="plan-details-trial-chip"
                    label="Trial"
                    size="small"
                    color="info"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem' }}
                  />
                )}
              </Box>
            </Box>
          </Box>

          {/* Price Section - Highlighted */}
          <Box 
            id="plan-details-price-section"
            sx={{ 
              mb: 1.5,
              p: 1.25,
              borderRadius: 2,
              backgroundColor: isDarkMode ? 'rgba(25, 118, 210, 0.08)' : 'rgba(25, 118, 210, 0.04)',
              border: `1px solid ${isDarkMode ? 'rgba(25, 118, 210, 0.2)' : 'rgba(25, 118, 210, 0.15)'}`,
            }}
          >
            <Typography id="plan-details-price" variant="h3" component="div" sx={{ fontWeight: 700, mb: 0.25, color: 'primary.main' }}>
              {formatPrice(activePlan.cost, activePlan.currency_code)}
              <Typography component="span" variant="body1" color="text.secondary" sx={{ ml: 0.75, fontWeight: 400 }}>
                /{activePlan.billing_cycle}
              </Typography>
            </Typography>
            <Typography id="plan-details-billing-cycle" variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              Billing Cycle: {activePlan.billing_cycle.charAt(0).toUpperCase() + activePlan.billing_cycle.slice(1)}
            </Typography>
          </Box>

          {/* Features Section */}
          {featuresArray.length > 0 && (
            <Box id="plan-details-features-section" sx={{ mb: 1.5 }}>
              <Typography id="plan-details-features-title" variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75, color: 'text.primary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                Features
              </Typography>
              <Stack 
                id="plan-details-features-list" 
                spacing={0.75}
                sx={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  pr: 1,
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                    borderRadius: '3px',
                    '&:hover': {
                      background: isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                    },
                  },
                }}
              >
                {featuresArray.map((feature, index) => (
                  <Box key={index} id={`plan-details-feature-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <CheckCircle
                      sx={{
                        fontSize: 20,
                        color: 'success.main',
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>{feature}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {/* Detailed Information
          {showDetailedInfo && (
            <>
              <Divider id="plan-details-divider" sx={{ my: 1.5 }} />
              <Typography id="plan-details-subscription-details-title" variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                Subscription Details
              </Typography>
              <Grid id="plan-details-subscription-details-grid" container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <Box 
                        id="plan-details-current-period-start"
                        sx={{ 
                          mb: 1,
                          p: 0.75,
                          borderRadius: 1,
                          backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                          <CalendarToday sx={{ fontSize: 18, color: 'primary.main' }} />
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                            Current Period Start
                          </Typography>
                        </Box>
                        <Typography id="plan-details-current-period-start-value" variant="body2" sx={{ fontWeight: 600, pl: 3.5, color: 'text.primary' }}>
                          {formatDateShort(activePlan.current_period_start)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box 
                        id="plan-details-current-period-end"
                        sx={{ 
                          mb: 1,
                          p: 0.75,
                          borderRadius: 1,
                          backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                          <Event sx={{ fontSize: 18, color: 'primary.main' }} />
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                            Current Period End
                          </Typography>
                        </Box>
                        <Typography id="plan-details-current-period-end-value" variant="body2" sx={{ fontWeight: 600, pl: 3.5, color: 'text.primary' }}>
                          {formatDateShort(activePlan.current_period_end)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box 
                        id="plan-details-next-renewal"
                        sx={{ 
                          mb: 1,
                          p: 0.75,
                          borderRadius: 1,
                          backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                          <CreditCard sx={{ fontSize: 18, color: 'primary.main' }} />
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                            Next Renewal
                          </Typography>
                        </Box>
                        <Typography id="plan-details-next-renewal-value" variant="body2" sx={{ fontWeight: 600, pl: 3.5, color: 'text.primary' }}>
                          {formatDateShort(activePlan.next_renewal)}
                        </Typography>
                      </Box>
                    </Grid>
                    {activePlan.is_trial && activePlan.trial_end && (
                      <Grid item xs={12} sm={6}>
                        <Box 
                          id="plan-details-trial-end"
                          sx={{ 
                            mb: 1,
                            p: 0.75,
                            borderRadius: 1,
                            backgroundColor: isDarkMode ? 'rgba(33, 150, 243, 0.08)' : 'rgba(33, 150, 243, 0.04)',
                            border: `1px solid ${isDarkMode ? 'rgba(33, 150, 243, 0.2)' : 'rgba(33, 150, 243, 0.15)'}`,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                            <Info sx={{ fontSize: 18, color: 'info.main' }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                              Trial End Date
                            </Typography>
                          </Box>
                          <Typography id="plan-details-trial-end-value" variant="body2" sx={{ fontWeight: 600, pl: 3.5, color: 'text.primary' }}>
                            {formatDateShort(activePlan.trial_end)}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </>
              )} */}

          {/* Upgrade Button */}
          {showUpgradeButton && (
            <>
              <Divider id="plan-details-upgrade-divider" sx={{ my: 1.5 }} />
              <Box id="plan-details-upgrade-section" sx={{ display: 'flex', justifyContent: 'center', pt: 0.5 }}>
                <Tooltip 
                  title={isUserRole ? "Please contact administrator to upgrade your plan" : ""}
                  arrow
                  placement="top"
                >
                  <span>
                    <Button
                      id="plan-details-upgrade-btn"
                      variant="contained"
                      color="primary"
                      onClick={handleUpgradeClick}
                      disabled={isUserRole}
                      startIcon={<TrendingUp />}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        py: 1,
                        borderRadius: 2,
                      }}
                    >
                      UPGRADE PLAN
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </>
          )}
            </CardContent>
  );

  return (
    <Box id="plan-details-container" sx={{ width: '100%' }}>
      {showCardBorder ? (
        <Card
          id="plan-details-card"
          sx={{
            border: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            transition: 'all 0.3s ease-in-out',
            boxShadow: isDarkMode ? 2 : 1,
          }}
        >
          {cardContent}
        </Card>
      ) : (
        <Box
          id="plan-details-card"
          sx={{
            backgroundColor: 'background.paper',
          }}
        >
          {cardContent}
        </Box>
      )}

      {/* Plans Modal */}
      <PlansModal
        open={plansModalOpen}
        onClose={() => setPlansModalOpen(false)}
        onPlanSelect={handlePlanSelect}
        currentPlanCode={activePlan?.plan_code}
        currentPlanId={
          activePlan?.plan_code
            ? availablePlans.find((p) => p.planCode === activePlan.plan_code)?.id
            : undefined
        }
        plans={availablePlans}
        autoFetch={false}
        showCurrentPlanBadge={true}
        gridColumns={{ xs: 1, md: 2, lg: 3 }}
        subscriptionId={getZohoSubscriptionId() || undefined}
      />
    </Box>
  );
};

export default PlanDetails;
